<template>
  <div v-if="!isLoading">
    <RouterView />
    <EulaModal v-if="userStore.currentCustomer?.has_unaccapted_eula"></EulaModal>
    <GlobalPreviewModal />
  </div>
  <div v-else>
    <div v-if="couldNotInitialize" class="d-flex align-items-center justify-content-center" style="height: 100vh">
      <LeadAlert class="tw:w-[400px]!" title="Error">
        {{ couldNotInitialize }}
      </LeadAlert>
    </div>
    <div v-else class="d-flex flex-column">
      <div class="page page-center">
        <div class="container py-5 w-25">
          <div class="text-center">
            <div class="mb-3">
              <img src="@/assets/logo/lighthouse1_free.png" width="260" height="260" alt="" />
            </div>
            <div class="text-secondary mb-3">{{ $t('app.init_loading') }}</div>
            <div class="progress progress-sm">
              <div class="progress-bar progress-bar-indeterminate"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { brand } from '@/config/brands';
import { nextTick, provide, ref } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import { createResetPasswordSession, destroySession } from '@/api/client';
import EulaModal from '@/components/user/EulaModal.vue';
import GlobalPreviewModal from './components/GlobalPreviewModal.vue';
import LeadAlert from '@/components/lead-design/LeadAlert.vue';

import translator, { availableLanguages, currentLanguage } from '@/locale/translator';
import { useUserStore } from '@/stores/user';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { useToast } from 'vue-toastification';
import { allowSelfRegistration } from '@/config/app';
export type AppLocale = {
  key: string;
  languageLabel: string;
  countryLabel: string;
};

/* ----------------------------- STATE ----------------------------- */
const toast = useToast();
const currentLanguageData = availableLanguages[currentLanguage];

const isLoading = ref(true);
const couldNotInitialize = ref<string | undefined>(undefined);

const userStore = useUserStore();
const featureStore = useCustomerFeaturesStore();

const route = useRoute();
const router = useRouter();

provide<AppLocale>('locale', {
  key: currentLanguage,
  languageLabel: currentLanguageData.label,
  countryLabel: currentLanguageData.country,
});

/* ----------------------------- METHODS ----------------------------- */

/** exchanges a reset-password token for a session, then routes to set-password (or sign-in on failure) */
async function handleResetPassword() {
  const { email, reset_token } = route.query as Record<string, string>;

  if (!email || !reset_token) {
    toast.error(translator.get('reset_password.wrong_link'));
    return nextTick(() => router.push({ name: 'login' }));
  }

  try {
    const res = await createResetPasswordSession({ email, reset_token });

    if (res.data?.id) {
      // await the refetch: the route guard needs the user in the store, else it bounces to sign-in
      await userStore.fetchCurrent();

      // unfinished registrants can't reach account settings and the wizard has no password field, so this is their recovery path
      if (userStore.hasUnfinishedRegistration) {
        router.push({ name: 'registration.setPassword' });
        return;
      }

      featureStore.fetchFeatures();

      router.push({
        name: 'settings.accounts.detail',
        params: { id: res.data?.id },
        query: { password: 1, did_reset: 1 },
      });
      return;
    }
  } catch {
    // fall through to sign-in
  }

  return nextTick(() => router.push({ name: 'login' }));
}

async function fetchCurrentUserAndEnforceLogin() {
  try {
    await userStore.ensureResolved();
    // route.meta is empty until the initial navigation lands, which the router guard holds on the same session
    await router.isReady();

    if (userStore.hasValidSession === false) {
      destroySession();

      setTimeout(() => {
        window.location.reload();
      }, 1000);

      return;
    }

    if (!userStore.isAuthenticated) {
      // the token becomes a session, so handle it before any redirect; stay on the loading screen meanwhile
      if (route.name === 'resetPassword') {
        await handleResetPassword();
        isLoading.value = false;
        return;
      }

      // public routes stay reachable while signed out
      if (route.meta.requiresAuth) {
        // stay on the loading screen until the redirect lands, so the protected page can't mount and race its own
        // on-mount navigation against the `redirect` query param
        await nextTick();
        await router.push({ name: 'login', query: { redirect: route.fullPath } });
        isLoading.value = false;
        return;
      }
    }

    // sign-in/reset-password pages are for strangers, and unfinished registrants belong in the wizard, not the dashboard
    if (
      userStore.isAuthenticated &&
      (route.meta.redirectAuthenticatedToHome ||
        (userStore.hasUnfinishedRegistration && route.meta.requiresAuth && !route.meta.availableWhileUnfinished))
    ) {
      router.push(userStore.authenticatedLandingRoute());
    }

    // finished registrants have no business in the wizard; mirrors the router.ts guard for the initial load
    if (
      (route.name === 'registration' || route.name === 'registration.setPassword') &&
      userStore.isAuthenticated &&
      !userStore.hasUnfinishedRegistration
    ) {
      router.push({ name: 'home' });
    }

    // self-registration is only offered on the appiq build; mirrors the router.ts guard for the initial load.
    // awaited so the wizard's on-mount query writes don't race the redirect
    if (route.name === 'registration' && !allowSelfRegistration && !userStore.isAuthenticated) {
      await router.push({ name: 'login' });
      isLoading.value = false;
      return;
    }

    // features are meaningless mid-registration and the request would be refused
    if (userStore.isAuthenticated && !userStore.hasUnfinishedRegistration) await featureStore.fetchFeatures();

    isLoading.value = false;
  } catch (error: any) {
    couldNotInitialize.value =
      translator.get('app.init_failure', { app: brand.name }) + (error.responseJson?.message ?? error.message);
  }
}

/* ----------------------------- LIFECYCLE ----------------------------- */
fetchCurrentUserAndEnforceLogin();
</script>
